import { useState } from 'react'
import { ActionIcon, Badge, Button, Group, Modal, Select, Stack, TextInput, Title, Tooltip } from '@mantine/core'
import { useDebouncedValue, useDisclosure } from '@mantine/hooks'
import { DataTable } from 'mantine-datatable'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { IconEdit, IconPaperclip, IconPlus, IconSearch } from '@tabler/icons-react'
import { ESTADOS, esEditableEstado, type Pago, type PagoEntrada, type PagoResumen } from '@serkat/shared'
import { ErrorApi, api, queryString } from '../../api/cliente'
import { useSesion } from '../auth/useSesion'
import { EstadoBadge } from '../../compartido/componentes/EstadoBadge'
import { PanelDocumento } from '../../compartido/componentes/PanelDocumento'
import { fecha, moneda } from '../../compartido/formato'
import { FormularioPago } from './componentes/FormularioPago'

const POR_PAGINA = 15
interface Pagina { datos: PagoResumen[]; total: number }

export function PagosPagina() {
  const { usuario } = useSesion()
  const qc = useQueryClient()
  const [pagina, setPagina] = useState(1)
  const [texto, setTexto] = useState('')
  const [buscar] = useDebouncedValue(texto, 300)
  const [estado, setEstado] = useState<string | null>(null)
  const [editando, setEditando] = useState<Pago | undefined>()
  const [abierto, modal] = useDisclosure(false)
  const [panel, setPanel] = useState<PagoResumen | null>(null)

  const filtro = { pagina, porPagina: POR_PAGINA, buscar: buscar || undefined, estado: estado || undefined }

  const { data, isFetching } = useQuery({
    queryKey: ['pagos', filtro],
    queryFn: () => api.get<Pagina>(`/pagos${queryString(filtro)}`),
    placeholderData: (previo) => previo,
  })

  const guardar = useMutation({
    mutationFn: (v: PagoEntrada) =>
      editando ? api.patch(`/pagos/${editando.id}`, v) : api.post('/pagos', v),
    onSuccess: () => {
      notifications.show({ color: 'teal', message: 'Aprobación de pago guardada' })
      void qc.invalidateQueries({ queryKey: ['pagos'] })
      modal.close()
    },
    onError: (e) =>
      notifications.show({
        color: 'red', title: 'No se pudo guardar',
        message: e instanceof ErrorApi ? e.message : 'Error inesperado',
      }),
  })

  const puedeEmitir = usuario?.puedeEmitir ?? false

  async function abrir(fila?: PagoResumen) {
    setEditando(fila ? await api.get<Pago>(`/pagos/${fila.id}`) : undefined)
    modal.open()
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={2}>Aprobación de pagos</Title>
        {puedeEmitir && (
          <Button leftSection={<IconPlus size={16} />} onClick={() => abrir()}>
            Nueva aprobación
          </Button>
        )}
      </Group>

      <Group align="flex-end">
        <TextInput
          placeholder="Buscar por número, orden, proveedor o factura"
          leftSection={<IconSearch size={16} />}
          value={texto}
          onChange={(e) => { setTexto(e.currentTarget.value); setPagina(1) }}
          w={380}
        />
        <Select placeholder="Todos los estados" clearable w={180}
          data={ESTADOS.map((e) => ({ value: e, label: e }))}
          value={estado} onChange={(v) => { setEstado(v); setPagina(1) }} />
      </Group>

      <DataTable
        withTableBorder borderRadius="sm" striped highlightOnHover minHeight={220}
        fetching={isFetching}
        records={data?.datos ?? []}
        totalRecords={data?.total ?? 0}
        recordsPerPage={POR_PAGINA}
        page={pagina}
        onPageChange={setPagina}
        noRecordsText="No hay aprobaciones de pago emitidas"
        idAccessor="id"
        columns={[
          {
            accessor: 'numero', title: 'Número', width: 150,
            render: (p) => <span style={{ fontFamily: 'monospace' }}>{p.numero}</span>,
          },
          {
            accessor: 'ordenNumero', title: 'Orden', width: 150,
            render: (p) => <span style={{ fontFamily: 'monospace' }}>{p.ordenNumero}</span>,
          },
          { accessor: 'proveedor', title: 'Proveedor', width: '24%' },
          {
            accessor: 'numeroArmada', title: 'Armada', width: 90, textAlign: 'center',
            render: (p) => (
              <Badge size="sm" variant="light" color="grape">
                {p.totalArmadas ? `${p.numeroArmada} de ${p.totalArmadas}` : p.numeroArmada}
              </Badge>
            ),
          },
          { accessor: 'numeroFactura', title: 'Factura', width: 120, render: (p) => p.numeroFactura ?? '—' },
          { accessor: 'fechaEmision', title: 'Emisión', width: 115, render: (p) => fecha(p.fechaEmision) },
          {
            accessor: 'monto', title: 'Monto', width: 140, textAlign: 'right',
            render: (p) => (
              <span style={{ fontFamily: 'monospace' }}>
                {moneda(p.monto, p.moneda as 'PEN' | 'USD')}
              </span>
            ),
          },
          { accessor: 'estado', title: 'Estado', width: 115, render: (p) => <EstadoBadge estado={p.estado} /> },
          {
            accessor: 'acciones', title: '', width: 92,
            render: (p) => (
              <Group gap={2} wrap="nowrap">
                <Tooltip label="Adjuntos e historial">
                  <ActionIcon variant="subtle" onClick={() => setPanel(p)}>
                    <IconPaperclip size={16} />
                  </ActionIcon>
                </Tooltip>
                {puedeEmitir && esEditableEstado(p.estado) && (
                  <Tooltip label="Editar">
                    <ActionIcon variant="subtle" onClick={() => void abrir(p)}>
                      <IconEdit size={16} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
            ),
          },
        ]}
      />

      <PanelDocumento
        documentoId={panel?.id ?? null}
        numero={panel?.numero ?? ''}
        estado={panel?.estado ?? 'Pendiente'}
        puedeEditar={puedeEmitir}
        onCerrar={() => setPanel(null)}
      />

      <Modal
        opened={abierto} onClose={modal.close}
        title={editando ? `Editar ${editando.numero}` : 'Nueva aprobación de pago'}
        size="85rem"
      >
        <FormularioPago
          inicial={editando}
          guardando={guardar.isPending}
          onGuardar={(v) => guardar.mutate(v)}
          onCancelar={modal.close}
        />
      </Modal>
    </Stack>
  )
}
